import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { api, CustomFieldDefinition, ControlHistoryEntry, ControlHistoryStats } from '../lib/api'
import { useDatePreferences } from '../hooks/useDatePreferences'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import { useConfirm } from '../components/ConfirmModal'
import Breadcrumb from '../components/Breadcrumb'
import TagsInput, { GRC_TAG_SUGGESTIONS } from '../components/TagsInput'
import EffectivenessRating from '../components/EffectivenessRating'
import CustomFieldsForm from '../components/CustomFieldsForm'
import {
  ArrowLeft,
  Edit2,
  Trash2,
  FileText,
  Save,
  User,
  Upload,
  X,
  Download,
  LinkIcon,
  Plus,
  Check,
  AlertCircle,
  Tag,
  Link2,
  Sparkles,
  ExternalLink,
  GitBranch,
  Building2,
  Server,
  Globe,
  ArrowRight,
  Lock,
  Unlock,
  Network,
  ChevronDown,
  ChevronRight,
  Search,
  History,
  Clock,
  Shield,
  Ban,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Camera,
} from 'lucide-react'
import ScreenCapture from '../components/ScreenCapture'

interface UserOption {
  id: string
  username: string
}

interface LinkedPolicy {
  id: string
  title: string
  status: string
  last_review_date?: number
  next_review_date?: number
  doc_type?: string
  linked_at?: number
}

interface Document {
  id: string
  title: string
  status: string
  category: string
}

interface Control {
  id: string
  control_number: string
  name: string
  description?: string
  frequency: string
  // The ONE status field - computed based on evidence/policy compliance
  status: string
  // User override flag to mark as N/A
  is_not_applicable?: boolean
  evidence_type?: 'evidence' | 'policy' | 'both'
  status_reason?: string
  days_until_due?: number | null
  is_overdue?: boolean
  framework_name?: string
  framework_code?: string
  owner_id?: string
  owner_username?: string
  evidence_count?: number
  direct_evidence_count?: number
  next_due_date?: number
  last_evidence_date?: number
  created_at: number
  updated_at: number
  recent_evidence?: Evidence[]
  linked_policies?: LinkedPolicy[]
  // Tags for categorization
  tags?: string[]
  // Effectiveness rating (1-5 scale)
  effectiveness_rating?: number | null
  // Custom fields
  custom_fields?: Record<string, any>
  // #311: Internal Control linkage (Common Control Framework)
  internal_control?: {
    id: string
    control_number: string
    name: string
    category: string
    subcategory?: string
    status: string
    evidence_count: number
    policy_count?: number
    evidence: Evidence[]
    // #312: Additional fields for dynamic status and sub-controls
    frequency?: string
    last_evidence_date?: number
    next_due_date?: number
    sub_controls?: {
      id: string
      control_number: string
      name: string
      status: string
      evidence_count: number
      policy_count?: number
      frequency?: string
      last_evidence_date?: number
      next_due_date?: number
    }[]
  }
}

interface Evidence {
  id: string
  note?: string
  timestamp: number
  file_name?: string
  file_key?: string
  source: string
  uploaded_by: string
  uploaded_by_username?: string
}

interface MappedControl {
  id: string
  mapping_id: string
  mapped_control_id: string
  mapped_control_number: string
  mapped_control_name: string
  mapped_framework_name: string
  ai_confidence: number
  mapping_type: 'equivalent' | 'partial' | 'related'
}

// Issue #154: Shared service inheritance
interface SharedService {
  id: string
  name: string
  description?: string
  service_type: 'internal' | 'third_party' | 'parent_company'
  provider_name?: string
  status: 'active' | 'inactive' | 'expired'
  soc_report_type?: string
  soc_report_period_end?: number
  linked_controls_count?: number
}

interface ControlInheritance {
  id: string
  shared_service_id: string
  shared_service_name: string
  service_type: 'internal' | 'third_party' | 'parent_company'
  inheritance_type: 'full' | 'partial' | 'complementary'
  user_responsibility?: string
  is_active: boolean
  notes?: string
}

// Issue #142: Control Dependencies
interface ControlDependency {
  id: string
  source_control_id: string
  target_control_id: string
  relationship_type: 'blocks' | 'supports'
  notes?: string
  target_control_number: string
  target_control_name: string
  target_framework_name: string
  target_status: string
  created_at: number
  created_by_username?: string
}

interface ControlDependent {
  id: string
  source_control_id: string
  target_control_id: string
  relationship_type: 'blocks' | 'supports'
  notes?: string
  source_control_number: string
  source_control_name: string
  source_framework_name: string
  source_status: string
  created_at: number
}

interface AvailableControl {
  id: string
  control_number: string
  name: string
  framework_name: string
  status: string
}

// Issue #72: Control Exception/Waiver
interface ControlException {
  id: string
  control_id: string
  exception_type: 'waiver' | 'exception' | 'compensating_control'
  reason: string
  risk_acceptance?: string
  compensating_control_description?: string
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'revoked'
  requested_by: string
  requested_by_name?: string
  approved_by?: string
  approved_by_name?: string
  approved_at?: number
  rejection_reason?: string
  valid_from: number
  valid_until?: number | null
  review_frequency?: 'monthly' | 'quarterly' | 'semi_annually' | 'annually' | null
  next_review_date?: number
  created_at: number
}

const statusOptions = [
  { value: 'pending', label: 'Pending', color: 'badge-yellow' },
  { value: 'in_progress', label: 'In Progress', color: 'badge-blue' },
  { value: 'compliant', label: 'Compliant', color: 'badge-green' },
  { value: 'non_compliant', label: 'Non-Compliant', color: 'badge-red' },
  { value: 'not_applicable', label: 'Not Applicable', color: 'badge-gray' },
]

export default function ControlDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const { formatDate: formatDatePref, formatDateTime: formatDateTimePref } = useDatePreferences()
  const [control, setControl] = useState<Control | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    is_not_applicable: false,
    frequency: '',
    owner_id: '',
    evidence_type: 'evidence' as 'evidence' | 'policy' | 'both',
    tags: [] as string[],
    effectiveness_rating: null as number | null,
    custom_fields: {} as Record<string, any>,
  })
  const [users, setUsers] = useState<UserOption[]>([])
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([])
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)

  // Policy linking state
  const [linkedPolicies, setLinkedPolicies] = useState<LinkedPolicy[]>([])
  const [showPolicyModal, setShowPolicyModal] = useState(false)
  const [availablePolicies, setAvailablePolicies] = useState<Document[]>([])
  const [loadingPolicies, setLoadingPolicies] = useState(false)
  const [linkingPolicy, setLinkingPolicy] = useState(false)

  // Mapped controls state
  const [mappedControls, setMappedControls] = useState<MappedControl[]>([])
  const [loadingMappings, setLoadingMappings] = useState(false)

  // Evidence upload state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadNote, setUploadNote] = useState('')
  const [showScreenCapture, setShowScreenCapture] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Issue #154: Shared service inheritance state
  const [controlInheritance, setControlInheritance] = useState<ControlInheritance[]>([])
  const [loadingInheritance, setLoadingInheritance] = useState(false)
  const [showInheritanceModal, setShowInheritanceModal] = useState(false)
  const [availableServices, setAvailableServices] = useState<SharedService[]>([])
  const [loadingServices, setLoadingServices] = useState(false)
  const [linkingService, setLinkingService] = useState(false)
  const [inheritanceForm, setInheritanceForm] = useState({
    shared_service_id: '',
    inheritance_type: 'full' as 'full' | 'partial' | 'complementary',
    user_responsibility: '',
    notes: '',
  })

  // Issue #142: Control Dependencies state
  const [dependencies, setDependencies] = useState<ControlDependency[]>([])
  const [dependents, setDependents] = useState<ControlDependent[]>([])
  const [loadingDependencies, setLoadingDependencies] = useState(false)
  const [showDependencyModal, setShowDependencyModal] = useState(false)
  const [availableControls, setAvailableControls] = useState<AvailableControl[]>([])
  const [loadingAvailableControls, setLoadingAvailableControls] = useState(false)
  const [addingDependency, setAddingDependency] = useState(false)
  const [dependencyForm, setDependencyForm] = useState({
    target_control_id: '',
    relationship_type: 'blocks' as 'blocks' | 'supports',
    notes: '',
  })
  const [blockingStatus, setBlockingStatus] = useState<{
    canBeCompliant: boolean
    blockingDependencies: Array<{ id: string; control_number: string; name: string; status: string }>
  } | null>(null)
  const [dependencySearchTerm, setDependencySearchTerm] = useState('')
  const [showImpactAnalysis, setShowImpactAnalysis] = useState(false)

  // Issue #94: Control History state
  const [history, setHistory] = useState<ControlHistoryEntry[]>([])
  const [historyStats, setHistoryStats] = useState<ControlHistoryStats | null>(null)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyFilter, setHistoryFilter] = useState<'all' | 'created' | 'updated' | 'status_change'>('all')

  // Issue #72: Control Exceptions state
  const [exceptions, setExceptions] = useState<ControlException[]>([])
  const [loadingExceptions, setLoadingExceptions] = useState(false)
  const [showExceptionModal, setShowExceptionModal] = useState(false)
  const [exceptionForm, setExceptionForm] = useState({
    exception_type: 'exception' as 'waiver' | 'exception' | 'compensating_control',
    reason: '',
    risk_acceptance: '',
    compensating_control_description: '',
    valid_from: Date.now(),
    valid_until: null as number | null,
    review_frequency: null as 'monthly' | 'quarterly' | 'semi_annually' | 'annually' | null,
  })
  const [submittingException, setSubmittingException] = useState(false)

  useEffect(() => {
    async function fetchControl() {
      if (!id) return
      try {
        const res = await api.getControl(id)
        if (res.success) {
          setControl(res.control)
          setEditForm({
            name: res.control.name || '',
            description: res.control.description || '',
            is_not_applicable: res.control.is_not_applicable || false,
            frequency: res.control.frequency || 'monthly',
            owner_id: res.control.owner_id || '',
            evidence_type: res.control.evidence_type || 'evidence',
            tags: res.control.tags || [],
            effectiveness_rating: res.control.effectiveness_rating || null,
            custom_fields: res.control.custom_fields || {},
          })
          // Set linked policies if present
          if (res.control.linked_policies) {
            setLinkedPolicies(res.control.linked_policies)
          }
        } else {
          setError('Control not found')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load control')
      } finally {
        setLoading(false)
      }
    }
    fetchControl()
  }, [id])

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await api.getUsers()
        if (res.success) {
          setUsers(res.users)
        }
      } catch (err) {
        console.error('Failed to load users:', err)
      }
    }
    fetchUsers()
  }, [])

  useEffect(() => {
    async function fetchMappedControls() {
      if (!id) return
      setLoadingMappings(true)
      try {
        const res = await api.getControlMappings(id)
        if (res.success) {
          setMappedControls(res.mappings || [])
        }
      } catch (err) {
        console.error('Failed to load control mappings:', err)
      } finally {
        setLoadingMappings(false)
      }
    }
    fetchMappedControls()
  }, [id])

  useEffect(() => {
    async function fetchCustomFieldDefinitions() {
      setLoadingCustomFields(true)
      try {
        const res = await api.getCustomFieldDefinitions('control')
        if (res.success) {
          setCustomFieldDefinitions(res.definitions)
        }
      } catch (err) {
        console.error('Failed to load custom field definitions:', err)
      } finally {
        setLoadingCustomFields(false)
      }
    }
    fetchCustomFieldDefinitions()
  }, [])

  // Issue #154: Fetch shared service inheritance
  useEffect(() => {
    async function fetchInheritance() {
      if (!id) return
      setLoadingInheritance(true)
      try {
        const res = await api.getControlInheritance(id)
        if (res.success) {
          setControlInheritance(res.inheritance || [])
        }
      } catch (err) {
        console.error('Failed to load control inheritance:', err)
      } finally {
        setLoadingInheritance(false)
      }
    }
    fetchInheritance()
  }, [id])

  // Issue #142: Fetch control dependencies and dependents
  useEffect(() => {
    async function fetchDependencies() {
      if (!id) return
      setLoadingDependencies(true)
      try {
        const [depsRes, dependentsRes] = await Promise.all([
          api.getControlDependencies(id),
          api.getControlDependents(id),
        ])
        if (depsRes.success) {
          setDependencies(depsRes.dependencies || [])
          setBlockingStatus(depsRes.blocking_status || null)
        }
        if (dependentsRes.success) {
          setDependents(dependentsRes.dependents || [])
        }
      } catch (err) {
        console.error('Failed to load control dependencies:', err)
      } finally {
        setLoadingDependencies(false)
      }
    }
    fetchDependencies()
  }, [id])

  // Issue #72: Fetch control exceptions
  useEffect(() => {
    async function fetchExceptions() {
      if (!id) return
      setLoadingExceptions(true)
      try {
        const res = await api.getControlExceptions(id)
        if (res.success) {
          setExceptions(res.exceptions || [])
        }
      } catch (err) {
        console.error('Failed to load control exceptions:', err)
      } finally {
        setLoadingExceptions(false)
      }
    }
    fetchExceptions()
  }, [id])

  const handleSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      const res = await api.updateControl(id, editForm)
      if (res.success) {
        setControl((prev) => (prev ? { ...prev, ...editForm } : null))
        setEditing(false)
        toast.success('Control updated successfully')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Delete Control',
      message: `Are you sure you want to delete "${control?.control_number}"? This action cannot be undone.`,
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      const res = await api.deleteControl(id)
      if (res.success) {
        toast.success('Control deleted successfully')
        navigate('/controls')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  // Refresh control to get updated evidence
  const refreshControl = async () => {
    if (!id) return
    try {
      const res = await api.getControl(id)
      if (res.success) {
        setControl(res.control)
      }
    } catch (err) {
      console.error('Failed to refresh control:', err)
    }
  }

  // Evidence upload handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0])
      setShowUploadModal(true)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
      setShowUploadModal(true)
    }
  }

  const handleEvidenceUpload = async () => {
    if (!id || (!selectedFile && !uploadNote.trim())) return

    setUploading(true)
    setUploadError('')

    try {
      const formData = new FormData()
      formData.append('control_id', id)
      if (selectedFile) {
        if (selectedFile.size > 50 * 1024 * 1024) {
          throw new Error('File too large. Maximum size is 50MB.')
        }
        formData.append('file', selectedFile)
      }
      if (uploadNote.trim()) {
        formData.append('note', uploadNote.trim())
      }

      const res = await api.uploadEvidence(formData)
      if (res.success) {
        setShowUploadModal(false)
        setSelectedFile(null)
        setUploadNote('')
        toast.success('Evidence uploaded successfully')
        refreshControl()
      }
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  // Handle screenshot capture (#95)
  const handleScreenCapture = (blob: Blob, fileName: string) => {
    const file = new File([blob], fileName, { type: 'image/png' })
    setSelectedFile(file)
    setShowScreenCapture(false)
    setShowUploadModal(true) // Open upload modal to complete the upload
  }

  const handleDeleteEvidence = async (evidenceId: string) => {
    const confirmed = await confirm({
      title: 'Delete Evidence',
      message: 'Are you sure you want to delete this evidence? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      const res = await api.deleteEvidence(evidenceId)
      if (res.success) {
        toast.success('Evidence deleted successfully')
        refreshControl()
      } else {
        toast.error('Failed to delete evidence')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete evidence')
    }
  }

  // Policy linking handlers
  const fetchAvailablePolicies = async () => {
    setLoadingPolicies(true)
    try {
      const res = await api.getDocuments({ status: 'approved' })
      if (res.success) {
        // Filter out already linked policies
        const linkedIds = new Set(linkedPolicies.map(p => p.id))
        setAvailablePolicies(res.documents.filter((d: Document) => !linkedIds.has(d.id)))
      }
    } catch (err) {
      console.error('Failed to fetch policies:', err)
    } finally {
      setLoadingPolicies(false)
    }
  }

  const handleOpenPolicyModal = () => {
    fetchAvailablePolicies()
    setShowPolicyModal(true)
  }

  const handleLinkPolicy = async (documentId: string) => {
    if (!id) return
    setLinkingPolicy(true)
    try {
      const res = await api.linkPolicyToControl(id, documentId)
      if (res.success) {
        // Refresh control to get updated linked policies and status
        await refreshControl()
        // Also fetch fresh linked policies
        const policiesRes = await api.getControlPolicies(id)
        if (policiesRes.success) {
          setLinkedPolicies(policiesRes.policies)
        }
        setShowPolicyModal(false)
        toast.success('Policy linked successfully')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to link policy')
    } finally {
      setLinkingPolicy(false)
    }
  }

  const handleUnlinkPolicy = async (documentId: string) => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Unlink Policy',
      message: 'Are you sure you want to unlink this policy from the control?',
      type: 'warning',
      confirmText: 'Unlink',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      const res = await api.unlinkPolicyFromControl(id, documentId)
      if (res.success) {
        setLinkedPolicies(prev => prev.filter(p => p.id !== documentId))
        // Refresh control to get updated status
        await refreshControl()
        toast.success('Policy unlinked successfully')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlink policy')
    }
  }

  // Issue #154: Shared service inheritance handlers
  const fetchAvailableServices = async () => {
    setLoadingServices(true)
    try {
      const res = await api.getSharedServices({ status: 'active' })
      if (res.success) {
        // Filter out already linked services
        const linkedIds = new Set(controlInheritance.map(i => i.shared_service_id))
        setAvailableServices(
          (res.shared_services || []).filter((s: SharedService) => !linkedIds.has(s.id))
        )
      }
    } catch (err) {
      console.error('Failed to fetch shared services:', err)
      toast.error('Failed to load shared services')
    } finally {
      setLoadingServices(false)
    }
  }

  const handleOpenInheritanceModal = () => {
    fetchAvailableServices()
    setInheritanceForm({
      shared_service_id: '',
      inheritance_type: 'full',
      user_responsibility: '',
      notes: '',
    })
    setShowInheritanceModal(true)
  }

  const handleAddInheritance = async () => {
    if (!id || !inheritanceForm.shared_service_id) return

    // Validate: partial and complementary require user_responsibility
    if (
      (inheritanceForm.inheritance_type === 'partial' || inheritanceForm.inheritance_type === 'complementary') &&
      !inheritanceForm.user_responsibility.trim()
    ) {
      toast.error('User responsibility is required for partial and complementary inheritance')
      return
    }

    setLinkingService(true)
    try {
      const res = await api.addControlInheritance(id, {
        shared_service_id: inheritanceForm.shared_service_id,
        inheritance_type: inheritanceForm.inheritance_type,
        user_responsibility: inheritanceForm.user_responsibility || undefined,
        notes: inheritanceForm.notes || undefined,
      })
      if (res.success) {
        // Refresh inheritance and control
        const inheritRes = await api.getControlInheritance(id)
        if (inheritRes.success) {
          setControlInheritance(inheritRes.inheritance || [])
        }
        await refreshControl()
        setShowInheritanceModal(false)
        toast.success('Shared service linked successfully')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to link shared service')
    } finally {
      setLinkingService(false)
    }
  }

  const handleRemoveInheritance = async (inheritanceId: string) => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Remove Inheritance',
      message: 'Are you sure you want to remove this shared service inheritance?',
      type: 'warning',
      confirmText: 'Remove',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      const res = await api.removeControlInheritance(id, inheritanceId)
      if (res.success) {
        setControlInheritance(prev => prev.filter(i => i.id !== inheritanceId))
        await refreshControl()
        toast.success('Inheritance removed')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove inheritance')
    }
  }

  const getServiceTypeIcon = (type: string) => {
    switch (type) {
      case 'internal': return Server
      case 'third_party': return Globe
      case 'parent_company': return Building2
      default: return Server
    }
  }

  const getInheritanceTypeColor = (type: string) => {
    switch (type) {
      case 'full': return 'bg-purple-100 text-purple-800'
      case 'partial': return 'bg-indigo-100 text-indigo-800'
      case 'complementary': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Issue #142: Control Dependencies handlers
  const fetchAvailableControlsForDependency = async () => {
    setLoadingAvailableControls(true)
    try {
      const res = await api.getControls({ limit: 200 })
      if (res.success) {
        // Filter out current control and already-linked controls
        const existingDepIds = new Set(dependencies.map(d => d.target_control_id))
        setAvailableControls(
          res.controls
            .filter((c: AvailableControl) => c.id !== id && !existingDepIds.has(c.id))
            .map((c: any) => ({
              id: c.id,
              control_number: c.control_number,
              name: c.name,
              framework_name: c.framework_name,
              status: c.status,
            }))
        )
      }
    } catch (err) {
      console.error('Failed to fetch controls:', err)
      toast.error('Failed to load available controls')
    } finally {
      setLoadingAvailableControls(false)
    }
  }

  const handleOpenDependencyModal = () => {
    fetchAvailableControlsForDependency()
    setDependencyForm({
      target_control_id: '',
      relationship_type: 'blocks',
      notes: '',
    })
    setDependencySearchTerm('')
    setShowDependencyModal(true)
  }

  const handleAddDependency = async () => {
    if (!id || !dependencyForm.target_control_id) return

    setAddingDependency(true)
    try {
      const res = await api.addControlDependency(id, {
        target_control_id: dependencyForm.target_control_id,
        relationship_type: dependencyForm.relationship_type,
        notes: dependencyForm.notes || undefined,
      })
      if (res.success) {
        // Refresh dependencies
        const depsRes = await api.getControlDependencies(id)
        if (depsRes.success) {
          setDependencies(depsRes.dependencies || [])
          setBlockingStatus(depsRes.blocking_status || null)
        }
        setShowDependencyModal(false)
        toast.success('Dependency added successfully')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add dependency')
    } finally {
      setAddingDependency(false)
    }
  }

  const handleRemoveDependency = async (depId: string) => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Remove Dependency',
      message: 'Are you sure you want to remove this dependency?',
      type: 'warning',
      confirmText: 'Remove',
      cancelText: 'Cancel',
    })

    if (!confirmed) return

    try {
      const res = await api.removeControlDependency(id, depId)
      if (res.success) {
        setDependencies(prev => prev.filter(d => d.id !== depId))
        // Re-fetch to update blocking status
        const depsRes = await api.getControlDependencies(id)
        if (depsRes.success) {
          setBlockingStatus(depsRes.blocking_status || null)
        }
        toast.success('Dependency removed')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove dependency')
    }
  }

  // Issue #72: Control Exception handlers
  const handleRequestException = async () => {
    if (!id) return
    setSubmittingException(true)
    try {
      const res = await api.requestControlException(id, {
        exception_type: exceptionForm.exception_type,
        reason: exceptionForm.reason,
        risk_acceptance: exceptionForm.risk_acceptance || undefined,
        compensating_control_description: exceptionForm.compensating_control_description || undefined,
        valid_from: exceptionForm.valid_from,
        valid_until: exceptionForm.valid_until,
        review_frequency: exceptionForm.review_frequency,
      })
      if (res.success) {
        // Refresh exceptions
        const exceptionsRes = await api.getControlExceptions(id)
        if (exceptionsRes.success) {
          setExceptions(exceptionsRes.exceptions || [])
        }
        setShowExceptionModal(false)
        setExceptionForm({
          exception_type: 'exception',
          reason: '',
          risk_acceptance: '',
          compensating_control_description: '',
          valid_from: Date.now(),
          valid_until: null,
          review_frequency: null,
        })
        toast.success('Exception request submitted')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to submit exception')
    } finally {
      setSubmittingException(false)
    }
  }

  const handleExceptionAction = async (
    exceptionId: string,
    action: 'approve' | 'reject' | 'revoke',
    rejectionReason?: string
  ) => {
    if (!id) return
    try {
      const res = await api.updateControlException(id, exceptionId, {
        action,
        rejection_reason: rejectionReason,
      })
      if (res.success) {
        // Refresh exceptions
        const exceptionsRes = await api.getControlExceptions(id)
        if (exceptionsRes.success) {
          setExceptions(exceptionsRes.exceptions || [])
        }
        toast.success(`Exception ${action}${action.endsWith('e') ? 'd' : 'ed'}`)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Failed to ${action} exception`)
    }
  }

  const handleDeleteException = async (exceptionId: string) => {
    if (!id) return

    const confirmed = await confirm({
      title: 'Delete Exception Request',
      message: 'Are you sure you want to delete this exception request?',
      type: 'warning',
      confirmText: 'Delete',
    })

    if (!confirmed) return

    try {
      const res = await api.deleteControlException(id, exceptionId)
      if (res.success) {
        setExceptions(prev => prev.filter(e => e.id !== exceptionId))
        toast.success('Exception request deleted')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete exception')
    }
  }

  const getExceptionStatusConfig = (status: string) => {
    switch (status) {
      case 'pending':
        return { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Pending Approval' }
      case 'approved':
        return { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-100', label: 'Approved' }
      case 'rejected':
        return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Rejected' }
      case 'expired':
        return { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-100', label: 'Expired' }
      case 'revoked':
        return { icon: Ban, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Revoked' }
      default:
        return { icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100', label: status }
    }
  }

  const getExceptionTypeLabel = (type: string) => {
    switch (type) {
      case 'waiver': return 'Waiver'
      case 'exception': return 'Exception'
      case 'compensating_control': return 'Compensating Control'
      default: return type
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800'
      case 'non_compliant': return 'bg-red-100 text-red-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'not_applicable': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRelationshipColor = (type: string) => {
    return type === 'blocks' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
  }

  const filteredAvailableControls = availableControls.filter(c =>
    dependencySearchTerm === '' ||
    c.control_number.toLowerCase().includes(dependencySearchTerm.toLowerCase()) ||
    c.name.toLowerCase().includes(dependencySearchTerm.toLowerCase()) ||
    c.framework_name.toLowerCase().includes(dependencySearchTerm.toLowerCase())
  )

  // Issue #94: Fetch control history
  const fetchHistory = async () => {
    if (!id) return
    setLoadingHistory(true)
    try {
      const filterParam = historyFilter === 'all' ? undefined : historyFilter
      const res = await api.getControlHistory(id, { limit: 50, change_type: filterParam as any })
      if (res.success) {
        setHistory(res.history)
        setHistoryStats(res.stats)
      }
    } catch (err) {
      console.error('Failed to fetch control history:', err)
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleOpenHistoryModal = () => {
    fetchHistory()
    setShowHistoryModal(true)
  }

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'created': return 'bg-green-100 text-green-800'
      case 'updated': return 'bg-blue-100 text-blue-800'
      case 'status_change': return 'bg-purple-100 text-purple-800'
      case 'deleted': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'created': return Plus
      case 'status_change': return AlertCircle
      case 'deleted': return Trash2
      default: return Edit2
    }
  }

  const formatDateTime = (ts: number) => {
    if (!ts) return '-'
    return formatDateTimePref(ts)
  }

  const renderFieldDiff = (fieldName: string, oldValue: any, newValue: any) => {
    const formatValue = (val: any): string => {
      if (val === null || val === undefined) return '(empty)'
      if (typeof val === 'boolean') return val ? 'Yes' : 'No'
      if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : '(none)'
      if (typeof val === 'object') return JSON.stringify(val)
      return String(val)
    }

    const fieldLabel = fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

    return (
      <div key={fieldName} className="py-2 border-b last:border-b-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
          {fieldLabel}
        </p>
        <div className="flex items-start gap-2 text-sm">
          <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded line-through">
            {formatValue(oldValue)}
          </span>
          <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
          <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded">
            {formatValue(newValue)}
          </span>
        </div>
      </div>
    )
  }

  const formatDate = (ts: number) => {
    if (!ts) return '-'
    return formatDatePref(ts)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error || !control) {
    return (
      <div className="space-y-4">
        <Link to="/controls" className="text-primary-600 hover:text-primary-700 flex items-center">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Controls
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'Control not found'}
        </div>
      </div>
    )
  }

  // Status is now the single unified field
  const statusInfo = statusOptions.find((s) => s.value === control.status) || statusOptions[0]

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Controls', href: '/controls' },
          { label: control.control_number },
        ]}
      />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/controls" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{control.control_number}</h1>
            <p className="text-gray-500">{control.framework_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={() => setEditing(false)}
                className="btn-secondary"
                disabled={saving}
              >
                Cancel
              </button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="btn-secondary">
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </button>
              <button onClick={handleDelete} className="btn-secondary text-red-600 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Control Details */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Control Details</h2>
            {editing ? (
              <div className="space-y-4">
                <div>
                  <label className="label">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="input"
                    rows={4}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Frequency</label>
                    <select
                      value={editForm.frequency}
                      onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}
                      className="input"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="semi-annually">Semi-Annually</option>
                      <option value="annually">Annually</option>
                      <option value="none">None</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">Not Applicable</label>
                    <div className="flex items-center mt-2">
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, is_not_applicable: !editForm.is_not_applicable })}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          editForm.is_not_applicable ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            editForm.is_not_applicable ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                      <span className="ml-3 text-sm text-gray-600">
                        {editForm.is_not_applicable ? 'Control marked as N/A' : 'Control is applicable'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Mark as N/A if this control does not apply to your organization
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">Owner</label>
                    <select
                      value={editForm.owner_id}
                      onChange={(e) => setEditForm({ ...editForm, owner_id: e.target.value })}
                      className="input"
                    >
                      <option value="">Unassigned</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Evidence Type</label>
                    <select
                      value={editForm.evidence_type}
                      onChange={(e) => setEditForm({ ...editForm, evidence_type: e.target.value as 'evidence' | 'policy' | 'both' })}
                      className="input"
                    >
                      <option value="evidence">Evidence Only</option>
                      <option value="policy">Policy Only</option>
                      <option value="both">Evidence + Policy</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {editForm.evidence_type === 'policy' && 'Compliance based on linked policy review status'}
                      {editForm.evidence_type === 'both' && 'Requires both evidence and policy to be current'}
                      {editForm.evidence_type === 'evidence' && 'Traditional evidence collection'}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="label flex items-center gap-1">
                    <Tag className="h-4 w-4" />
                    Tags
                  </label>
                  <TagsInput
                    value={editForm.tags}
                    onChange={(tags) => setEditForm({ ...editForm, tags })}
                    placeholder="Add tags (e.g., access-control, encryption)..."
                    suggestions={GRC_TAG_SUGGESTIONS}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Press Enter or comma to add a tag
                  </p>
                </div>
                <div>
                  <label className="label">Effectiveness Rating</label>
                  <EffectivenessRating
                    value={editForm.effectiveness_rating}
                    onChange={(rating) => setEditForm({ ...editForm, effectiveness_rating: rating })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Rate how effectively this control mitigates risk (1-5)
                  </p>
                </div>

                {/* Custom Fields */}
                <CustomFieldsForm
                  fields={customFieldDefinitions}
                  values={editForm.custom_fields}
                  onChange={(fieldName, value) =>
                    setEditForm({
                      ...editForm,
                      custom_fields: { ...editForm.custom_fields, [fieldName]: value },
                    })
                  }
                  loading={loadingCustomFields}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Name</label>
                  <p className="text-gray-900">{control.name}</p>
                </div>
                {control.description && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Description</label>
                    <p className="text-gray-900 whitespace-pre-wrap">{control.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p>
                      <span className={statusInfo.color}>{statusInfo.label}</span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Frequency</label>
                    <p className="text-gray-900 capitalize">{control.frequency}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Owner</label>
                  <p className="text-gray-900 flex items-center">
                    <User className="h-4 w-4 mr-1 text-gray-400" />
                    {control.owner_username || 'Unassigned'}
                  </p>
                </div>
                {control.tags && control.tags.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {control.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 text-xs rounded-md bg-primary-100 text-primary-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {control.effectiveness_rating && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Effectiveness</label>
                    <div className="mt-1">
                      <EffectivenessRating
                        value={control.effectiveness_rating}
                        readonly
                        size="sm"
                      />
                    </div>
                  </div>
                )}
                {/* Display Custom Fields (read-only) */}
                {control.custom_fields && Object.keys(control.custom_fields).length > 0 && customFieldDefinitions.length > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Custom Fields</h3>
                    <div className="space-y-3">
                      {customFieldDefinitions.filter(f => f.active).map((field) => {
                        const value = control.custom_fields?.[field.field_name]
                        if (value === undefined || value === null || value === '') return null

                        let displayValue = value
                        if (field.field_type === 'checkbox') {
                          displayValue = value ? 'Yes' : 'No'
                        } else if (field.field_type === 'select' && field.options) {
                          const option = field.options.find(o => o.value === value)
                          displayValue = option ? option.label : value
                        } else if (field.field_type === 'date' && value) {
                          displayValue = formatDate(new Date(value).getTime())
                        }

                        return (
                          <div key={field.id}>
                            <label className="text-sm font-medium text-gray-500">{field.field_label}</label>
                            <p className="text-gray-900">{displayValue}</p>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recent Evidence */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Evidence ({control.evidence_count || 0})
              </h2>
              <div className="flex gap-2">
                {/* #311: Only show upload buttons if NO internal control is linked */}
                {!control.internal_control && (
                  <>
                    <button
                      onClick={() => setShowScreenCapture(true)}
                      className="btn-secondary text-sm"
                    >
                      <Camera className="h-4 w-4 mr-1" />
                      Capture
                    </button>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="btn-primary text-sm"
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </button>
                  </>
                )}
                <Link
                  to={`/evidence?control_id=${control.id}`}
                  className="btn-secondary text-sm"
                >
                  View All
                </Link>
              </div>
            </div>

            {/* #311: Show message to upload via internal control when linked */}
            {control.internal_control ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Link2 className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Evidence is managed via Internal Control
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      This control is satisfied by <span className="font-mono font-medium">{control.internal_control.control_number}</span>.
                      Upload evidence to the internal control to satisfy this and all other mapped framework requirements.
                    </p>
                    <Link
                      to="/internal-controls"
                      className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      <Upload className="h-4 w-4" />
                      Upload to {control.internal_control.control_number}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Drop zone - only show when no internal control */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors mb-4 ${
                    dragActive
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-primary-500' : 'text-gray-400'}`} />
                  <p className="text-sm text-gray-600">
                    Drag & drop files here or click to browse
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Max file size: 50MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </>
            )}

            {/* #311: Internal Control Evidence Section */}
            {control.internal_control && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Link2 className="h-4 w-4 text-primary-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Satisfied by Internal Control
                  </span>
                </div>
                <Link
                  to={`/internal-controls?search=${encodeURIComponent(control.internal_control.control_number)}`}
                  className="block p-4 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-sm text-primary-600">
                        {control.internal_control.control_number}
                      </span>
                      <h4 className="font-medium text-gray-900">
                        {control.internal_control.name}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        {control.internal_control.category}
                        {control.internal_control.subcategory && ` › ${control.internal_control.subcategory}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        control.internal_control.status === 'compliant'
                          ? 'bg-green-100 text-green-700'
                          : control.internal_control.status === 'non_compliant'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {control.internal_control.status === 'compliant' ? 'Compliant' :
                         control.internal_control.status === 'non_compliant' ? 'Non-Compliant' :
                         'Pending'}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {control.internal_control.evidence_count} evidence, {control.internal_control.policy_count || 0} policies
                      </p>
                    </div>
                  </div>
                </Link>

                {/* Internal Control Evidence */}
                {control.internal_control.evidence && control.internal_control.evidence.length > 0 && (
                  <div className="mt-3 pl-4 border-l-2 border-primary-200">
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Evidence via Internal Control:
                    </p>
                    <div className="space-y-2">
                      {control.internal_control.evidence.slice(0, 5).map((evidence) => (
                        <div
                          key={evidence.id}
                          className="flex items-start p-2 bg-white rounded border border-gray-100"
                        >
                          <FileText className="h-4 w-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 truncate">
                              {evidence.note || evidence.file_name || 'Evidence'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(evidence.timestamp)} &middot; {evidence.source}
                            </p>
                          </div>
                        </div>
                      ))}
                      {control.internal_control.evidence_count > 5 && (
                        <p className="text-xs text-gray-500 text-center">
                          +{control.internal_control.evidence_count - 5} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* #312: Sub-controls section */}
                {control.internal_control.sub_controls && control.internal_control.sub_controls.length > 0 && (
                  <div className="mt-4 border-t border-primary-200 pt-4">
                    <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                      <GitBranch className="h-3.5 w-3.5" />
                      Granular Sub-Controls ({control.internal_control.sub_controls.length})
                    </p>
                    <div className="space-y-2">
                      {control.internal_control.sub_controls.map((subControl) => (
                        <Link
                          key={subControl.id}
                          to={`/internal-controls?search=${encodeURIComponent(subControl.control_number)}`}
                          className="flex items-center justify-between p-2.5 bg-white border border-gray-200 rounded hover:border-primary-300 hover:bg-primary-50 transition-colors"
                        >
                          <div>
                            <span className="font-mono text-xs text-primary-600">
                              {subControl.control_number}
                            </span>
                            <p className="text-sm text-gray-800">{subControl.name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              {subControl.evidence_count} evidence, {subControl.policy_count || 0} policies
                            </span>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              subControl.status === 'compliant'
                                ? 'bg-green-100 text-green-700'
                                : subControl.status === 'non_compliant'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {subControl.status === 'compliant' ? 'Compliant' :
                               subControl.status === 'non_compliant' ? 'Non-Compliant' :
                               'Pending'}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Direct Evidence for this Framework Control */}
            {control.direct_evidence_count !== undefined && control.direct_evidence_count > 0 && control.internal_control && (
              <p className="text-xs font-medium text-gray-500 mb-2">
                Direct Evidence ({control.direct_evidence_count}):
              </p>
            )}

            {control.recent_evidence && control.recent_evidence.length > 0 ? (
              <div className="space-y-3">
                {control.recent_evidence.map((evidence) => (
                  <div
                    key={evidence.id}
                    className="flex items-start p-3 bg-gray-50 rounded-lg group"
                  >
                    <FileText className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        {evidence.note || evidence.file_name || 'Evidence'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(evidence.timestamp)} &middot; {evidence.source}
                        {evidence.uploaded_by_username && ` &middot; ${evidence.uploaded_by_username}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {evidence.file_key && (
                        <a
                          href={api.getEvidenceDownloadUrl(evidence.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded"
                          title="Download"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDeleteEvidence(evidence.id)}
                        className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">No evidence collected yet</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Current Status</span>
                <span className={statusInfo.color}>{statusInfo.label}</span>
              </div>
              {control.status_reason && (
                <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  {control.status_reason}
                </div>
              )}
              {control.is_overdue && (
                <div className="text-xs text-red-600 bg-red-50 p-2 rounded font-medium">
                  Evidence collection is overdue
                </div>
              )}
              {control.days_until_due !== null && control.days_until_due !== undefined && control.days_until_due > 0 && control.days_until_due <= 14 && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded font-medium">
                  Evidence due in {control.days_until_due} day{control.days_until_due !== 1 ? 's' : ''}
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Evidence Count</span>
                <span className="text-sm font-medium text-gray-900">
                  {/* #312: Show IC evidence count when control is satisfied via internal control */}
                  {control.internal_control
                    ? control.internal_control.evidence_count + (control.direct_evidence_count || 0)
                    : control.evidence_count || 0}
                  {control.internal_control && control.internal_control.evidence_count > 0 && (
                    <span className="text-xs text-gray-500 ml-1">
                      (via {control.internal_control.control_number})
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Last Evidence</span>
                <span className="text-sm text-gray-900">
                  {/* #312: Show IC last evidence date when control is satisfied via internal control */}
                  {control.internal_control && control.internal_control.last_evidence_date
                    ? formatDate(control.internal_control.last_evidence_date)
                    : formatDate(control.last_evidence_date || 0)}
                </span>
              </div>
              {/* #312: Show Next Due based on IC frequency when control is satisfied via internal control */}
              {(() => {
                const effectiveFrequency = control.internal_control?.frequency || control.frequency
                const effectiveNextDue = control.internal_control?.next_due_date || control.next_due_date

                if (effectiveFrequency === 'none') {
                  return (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Next Due</span>
                      <span className="text-sm text-gray-500 italic">N/A (no recurring requirement)</span>
                    </div>
                  )
                }

                return (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Next Due</span>
                    <span className={`text-sm ${control.is_overdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                      {effectiveNextDue
                        ? formatDate(effectiveNextDue)
                        : control.days_until_due !== null && control.days_until_due !== undefined
                          ? formatDate(Date.now() + control.days_until_due * 24 * 60 * 60 * 1000)
                          : '-'}
                    </span>
                  </div>
                )
              })()}
            </div>
          </div>

          {/* Linked Policies - show when there are linked policies OR evidence_type requires it (#286) */}
          {(linkedPolicies.length > 0 || control.evidence_type === 'policy' || control.evidence_type === 'both') && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Linked Policies</h2>
                <button
                  onClick={handleOpenPolicyModal}
                  className="text-primary-600 hover:text-primary-700 p-1"
                  title="Link Policy"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>

              {linkedPolicies.length > 0 ? (
                <div className="space-y-3">
                  {linkedPolicies.map((policy) => {
                    const isApproved = policy.status === 'approved'
                    const isReviewDue = policy.next_review_date && policy.next_review_date < Date.now()
                    return (
                      <div
                        key={policy.id}
                        className="flex items-start p-3 bg-gray-50 rounded-lg group"
                      >
                        <FileText className={`h-5 w-5 mr-3 mt-0.5 ${isApproved ? 'text-green-500' : 'text-amber-500'}`} />
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/documents/${policy.id}`}
                            className="text-sm font-medium text-gray-900 hover:text-primary-600"
                          >
                            {policy.title}
                          </Link>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              isApproved ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {policy.status}
                            </span>
                            {isReviewDue && (
                              <span className="text-xs text-red-600 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-0.5" />
                                Review overdue
                              </span>
                            )}
                          </div>
                          {policy.next_review_date && !isReviewDue && (
                            <p className="text-xs text-gray-500 mt-1">
                              Review due: {formatDate(policy.next_review_date)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleUnlinkPolicy(policy.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Unlink policy"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <LinkIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No policies linked</p>
                  <button
                    onClick={handleOpenPolicyModal}
                    className="text-sm text-primary-600 hover:text-primary-700 mt-2"
                  >
                    Link a policy
                  </button>
                </div>
              )}

              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Evidence Type</span>
                  <span className="text-gray-900 capitalize font-medium">
                    {control.evidence_type === 'policy' ? 'Policy Only' : 'Evidence + Policy'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Issue #154: Shared Services Inheritance */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <GitBranch className="h-5 w-5 text-purple-500" />
                Shared Services
              </h2>
              <button
                onClick={handleOpenInheritanceModal}
                className="text-primary-600 hover:text-primary-700 p-1"
                title="Link Shared Service"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            {loadingInheritance ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : controlInheritance.length > 0 ? (
              <div className="space-y-3">
                {controlInheritance.map((inheritance) => {
                  const ServiceIcon = getServiceTypeIcon(inheritance.service_type)
                  return (
                    <div
                      key={inheritance.id}
                      className="flex items-start p-3 bg-gray-50 rounded-lg group"
                    >
                      <ServiceIcon className="h-5 w-5 text-purple-500 mr-3 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {inheritance.shared_service_name}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${getInheritanceTypeColor(inheritance.inheritance_type)}`}>
                            {inheritance.inheritance_type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 capitalize">
                          {inheritance.service_type.replace('_', ' ')}
                        </p>
                        {inheritance.user_responsibility && (
                          <p className="text-xs text-gray-600 mt-1 italic">
                            Your responsibility: {inheritance.user_responsibility}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemoveInheritance(inheritance.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove inheritance"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )
                })}
                <div className="text-xs text-gray-500 bg-purple-50 p-2 rounded border border-purple-100">
                  {controlInheritance.some(i => i.inheritance_type === 'full')
                    ? 'Control status is fully inherited from the shared service.'
                    : 'Shared service provides partial compliance coverage.'}
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <GitBranch className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No shared services linked</p>
                <p className="text-xs text-gray-400 mt-1">
                  Link a shared service to inherit compliance from a parent company, internal service, or third-party provider.
                </p>
                <button
                  onClick={handleOpenInheritanceModal}
                  className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block"
                >
                  Link Shared Service
                </button>
              </div>
            )}
          </div>

          {/* Issue #142: Control Dependencies */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Network className="h-5 w-5 text-orange-500" />
                Dependencies
              </h2>
              <button
                onClick={handleOpenDependencyModal}
                className="text-primary-600 hover:text-primary-700 p-1"
                title="Add Dependency"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            {/* Blocking Status Warning */}
            {blockingStatus && !blockingStatus.canBeCompliant && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Lock className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Compliance Blocked</p>
                    <p className="text-xs text-red-600 mt-1">
                      This control cannot be marked compliant until {blockingStatus.blockingDependencies.length} blocking{' '}
                      {blockingStatus.blockingDependencies.length === 1 ? 'dependency is' : 'dependencies are'} compliant:
                    </p>
                    <ul className="text-xs text-red-600 mt-1 list-disc list-inside">
                      {blockingStatus.blockingDependencies.map(dep => (
                        <li key={dep.id}>
                          <Link to={`/controls/${dep.id}`} className="underline hover:text-red-800">
                            {dep.control_number}
                          </Link>
                          {' - '}{dep.status}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {loadingDependencies ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : (
              <>
                {/* Dependencies (this control depends on) */}
                {dependencies.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      Depends On ({dependencies.length})
                    </h3>
                    <div className="space-y-2">
                      {dependencies.map(dep => (
                        <div
                          key={dep.id}
                          className="flex items-start p-2 bg-gray-50 rounded-lg group"
                        >
                          {dep.relationship_type === 'blocks' ? (
                            <Lock className="h-4 w-4 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Unlock className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/controls/${dep.target_control_id}`}
                                className="text-sm font-medium text-gray-900 hover:text-primary-600"
                              >
                                {dep.target_control_number}
                              </Link>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${getRelationshipColor(dep.relationship_type)}`}>
                                {dep.relationship_type}
                              </span>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusColor(dep.target_status)}`}>
                                {dep.target_status}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {dep.target_control_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {dep.target_framework_name}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveDependency(dep.id)}
                            className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Remove dependency"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dependents (controls that depend on this) */}
                {dependents.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <ArrowLeft className="h-3 w-3" />
                      Depended On By ({dependents.length})
                    </h3>
                    <div className="space-y-2">
                      {dependents.map(dep => (
                        <div
                          key={dep.id}
                          className="flex items-start p-2 bg-gray-50 rounded-lg"
                        >
                          {dep.relationship_type === 'blocks' ? (
                            <Lock className="h-4 w-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Unlock className="h-4 w-4 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/controls/${dep.source_control_id}`}
                                className="text-sm font-medium text-gray-900 hover:text-primary-600"
                              >
                                {dep.source_control_number}
                              </Link>
                              <span className={`text-xs px-1.5 py-0.5 rounded ${getRelationshipColor(dep.relationship_type)}`}>
                                {dep.relationship_type}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate">
                              {dep.source_control_name}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dependencies.length === 0 && dependents.length === 0 && (
                  <div className="text-center py-6">
                    <Network className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No dependencies</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add dependencies to define prerequisite relationships between controls.
                    </p>
                    <button
                      onClick={handleOpenDependencyModal}
                      className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block"
                    >
                      Add Dependency
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Issue #72: Control Exceptions/Waivers */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-500" />
                Exceptions & Waivers
              </h2>
              <button
                onClick={() => setShowExceptionModal(true)}
                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Request Exception
              </button>
            </div>

            {loadingExceptions ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : exceptions.length > 0 ? (
              <div className="space-y-3">
                {exceptions.map((exception) => {
                  const statusConfig = getExceptionStatusConfig(exception.status)
                  const StatusIcon = statusConfig.icon
                  const isExpiring = exception.valid_until && exception.valid_until < Date.now() + 30 * 24 * 60 * 60 * 1000

                  return (
                    <div
                      key={exception.id}
                      className={`p-3 rounded-lg border ${
                        exception.status === 'approved' && isExpiring
                          ? 'border-orange-200 bg-orange-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.color}`}>
                              <StatusIcon className="h-3 w-3" />
                              {statusConfig.label}
                            </span>
                            <span className="text-xs text-gray-500 capitalize">
                              {getExceptionTypeLabel(exception.exception_type)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mt-2 line-clamp-2">
                            {exception.reason}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>By {exception.requested_by_name}</span>
                            {exception.valid_until && (
                              <span className={`flex items-center gap-1 ${isExpiring ? 'text-orange-600' : ''}`}>
                                <Calendar className="h-3 w-3" />
                                Expires {formatDate(exception.valid_until)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 ml-2">
                          {exception.status === 'pending' && (
                            <>
                              {user?.role === 'admin' && (
                                <>
                                  <button
                                    onClick={() => handleExceptionAction(exception.id, 'approve')}
                                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const reason = prompt('Rejection reason (required):')
                                      if (reason) {
                                        await handleExceptionAction(exception.id, 'reject', reason)
                                      }
                                    }}
                                    className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeleteException(exception.id)}
                                className="text-xs px-2 py-1 text-gray-600 hover:text-red-600"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {exception.status === 'approved' && user?.role === 'admin' && (
                            <button
                              onClick={() => handleExceptionAction(exception.id, 'revoke')}
                              className="text-xs px-2 py-1 text-red-600 hover:text-red-700"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>
                      {exception.rejection_reason && (
                        <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                          <strong>Rejection reason:</strong> {exception.rejection_reason}
                        </div>
                      )}
                      {exception.compensating_control_description && (
                        <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                          <strong>Compensating Control:</strong> {exception.compensating_control_description}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <Shield className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No exceptions or waivers</p>
                <p className="text-xs text-gray-400 mt-1">
                  Request an exception if this control cannot be fully implemented.
                </p>
              </div>
            )}
          </div>

          {/* Mapped Controls */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary-500" />
                Mapped Controls
              </h2>
              <Link
                to="/mappings"
                className="text-xs text-primary-600 hover:text-primary-700"
              >
                Manage All
              </Link>
            </div>

            {loadingMappings ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : mappedControls.length > 0 ? (
              <div className="space-y-3">
                {mappedControls.map((mapping) => {
                  const typeColors = {
                    equivalent: 'bg-green-100 text-green-800',
                    partial: 'bg-yellow-100 text-yellow-800',
                    related: 'bg-blue-100 text-blue-800',
                  }
                  const typeColor = typeColors[mapping.mapping_type] || typeColors.related

                  return (
                    <div
                      key={mapping.id || mapping.mapping_id}
                      className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <Link
                            to={`/controls/${mapping.mapped_control_id}`}
                            className="font-medium text-gray-900 hover:text-primary-600 flex items-center gap-1"
                          >
                            {mapping.mapped_control_number}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <p className="text-sm text-gray-500 truncate">
                            {mapping.mapped_control_name}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {mapping.mapped_framework_name}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-2">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${typeColor}`}>
                            {mapping.mapping_type}
                          </span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Sparkles className="h-3 w-3" />
                            {Math.round(mapping.ai_confidence * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {mappedControls.length > 0 && (
                  <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded border border-blue-100">
                    Evidence uploaded to this control can be linked to {mappedControls.length} mapped control{mappedControls.length !== 1 ? 's' : ''}.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Link2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No mapped controls</p>
                <p className="text-xs text-gray-400 mt-1">
                  Use AI analysis on the Mappings page to find equivalent controls
                </p>
                <Link
                  to="/mappings"
                  className="text-sm text-primary-600 hover:text-primary-700 mt-3 inline-block"
                >
                  Go to Control Mappings
                </Link>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Framework</span>
                <span className="text-gray-900">{control.framework_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Owner</span>
                <span className="text-gray-900">{control.owner_username || 'Unassigned'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Evidence Type</span>
                <span className="text-gray-900 capitalize">
                  {control.evidence_type === 'policy' ? 'Policy' :
                   control.evidence_type === 'both' ? 'Evidence + Policy' : 'Evidence'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">{formatDate(control.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Updated</span>
                <span className="text-gray-900">{formatDate(control.updated_at)}</span>
              </div>
            </div>
          </div>

          {/* Issue #94: Control History */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <History className="h-5 w-5 text-indigo-500" />
                Change History
              </h2>
              <button
                onClick={handleOpenHistoryModal}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                View All
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Track all changes made to this control over time.
            </p>
            <button
              onClick={handleOpenHistoryModal}
              className="w-full btn-secondary flex items-center justify-center gap-2"
            >
              <Clock className="h-4 w-4" />
              View Change Timeline
            </button>
          </div>
        </div>
      </div>

      {/* Evidence Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Upload Evidence</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false)
                  setSelectedFile(null)
                  setUploadNote('')
                  setUploadError('')
                }}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {uploadError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {uploadError}
                </div>
              )}

              <div>
                <label className="label">Control</label>
                <p className="text-sm font-medium text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                  {control.control_number} - {control.name}
                </p>
              </div>

              <div>
                <label className="label">File</label>
                {selectedFile ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="h-5 w-5 text-gray-500 mr-2" />
                      <span className="text-sm text-gray-900">{selectedFile.name}</span>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary-500 transition-colors"
                  >
                    <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-500 mt-1">Max file size: 50MB</p>
                  </div>
                )}
              </div>

              <div>
                <label className="label">Note (optional)</label>
                <textarea
                  value={uploadNote}
                  onChange={(e) => setUploadNote(e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Add a description or note about this evidence..."
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowUploadModal(false)
                    setSelectedFile(null)
                    setUploadNote('')
                    setUploadError('')
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEvidenceUpload}
                  disabled={uploading || (!selectedFile && !uploadNote.trim())}
                  className="btn-primary disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screen Capture Modal (#95) */}
      {showScreenCapture && (
        <ScreenCapture
          onCapture={handleScreenCapture}
          onCancel={() => setShowScreenCapture(false)}
        />
      )}

      {/* Policy Selection Modal */}
      {showPolicyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Link Policy to Control</h2>
              <button
                onClick={() => setShowPolicyModal(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-sm text-gray-600 mb-4">
                Select an approved policy to link to this control. The control's compliance status
                will be based on the policy's review status.
              </p>

              {loadingPolicies ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : availablePolicies.length > 0 ? (
                <div className="space-y-2">
                  {availablePolicies.map((doc) => (
                    <button
                      key={doc.id}
                      onClick={() => handleLinkPolicy(doc.id)}
                      disabled={linkingPolicy}
                      className="w-full flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left disabled:opacity-50"
                    >
                      <FileText className="h-5 w-5 text-green-500 mr-3" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{doc.title}</p>
                        <p className="text-xs text-gray-500 capitalize">{doc.category}</p>
                      </div>
                      <Check className="h-5 w-5 text-gray-300" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No approved policies available</p>
                  <p className="text-sm text-gray-500 mt-1">
                    All approved policies are already linked, or no policies have been approved yet.
                  </p>
                  <Link
                    to="/documents"
                    className="text-sm text-primary-600 hover:text-primary-700 mt-4 inline-block"
                  >
                    Go to Documents
                  </Link>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowPolicyModal(false)}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #154: Shared Service Inheritance Modal */}
      {showInheritanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <GitBranch className="h-5 w-5 text-purple-500" />
                Link Shared Service
              </h2>
              <button
                onClick={() => setShowInheritanceModal(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 flex-1 overflow-y-auto space-y-4">
              <p className="text-sm text-gray-600">
                Link a shared service to inherit compliance status. Full inheritance means the control
                is entirely satisfied by the shared service. Partial or complementary requires
                you to define your additional responsibilities.
              </p>

              {loadingServices ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : availableServices.length > 0 ? (
                <>
                  <div>
                    <label className="label">Shared Service</label>
                    <select
                      value={inheritanceForm.shared_service_id}
                      onChange={(e) => setInheritanceForm({ ...inheritanceForm, shared_service_id: e.target.value })}
                      className="input"
                    >
                      <option value="">Select a shared service...</option>
                      {availableServices.map((service) => {
                        const ServiceIcon = getServiceTypeIcon(service.service_type)
                        return (
                          <option key={service.id} value={service.id}>
                            {service.name} ({service.service_type.replace('_', ' ')})
                          </option>
                        )
                      })}
                    </select>
                  </div>

                  <div>
                    <label className="label">Inheritance Type</label>
                    <select
                      value={inheritanceForm.inheritance_type}
                      onChange={(e) => setInheritanceForm({
                        ...inheritanceForm,
                        inheritance_type: e.target.value as 'full' | 'partial' | 'complementary'
                      })}
                      className="input"
                    >
                      <option value="full">Full - Control fully satisfied by shared service</option>
                      <option value="partial">Partial - Shared service covers part, you have additional responsibilities</option>
                      <option value="complementary">Complementary - Your control builds on the shared service</option>
                    </select>
                  </div>

                  {(inheritanceForm.inheritance_type === 'partial' || inheritanceForm.inheritance_type === 'complementary') && (
                    <div>
                      <label className="label">
                        Your Responsibility <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={inheritanceForm.user_responsibility}
                        onChange={(e) => setInheritanceForm({ ...inheritanceForm, user_responsibility: e.target.value })}
                        rows={3}
                        className="input"
                        placeholder="Describe what additional controls or evidence you must maintain..."
                      />
                    </div>
                  )}

                  <div>
                    <label className="label">Notes (optional)</label>
                    <textarea
                      value={inheritanceForm.notes}
                      onChange={(e) => setInheritanceForm({ ...inheritanceForm, notes: e.target.value })}
                      rows={2}
                      className="input"
                      placeholder="Any additional notes about this inheritance..."
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <GitBranch className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No shared services available</p>
                  <p className="text-sm text-gray-500 mt-1">
                    All active shared services are already linked, or no shared services have been created.
                  </p>
                  <Link
                    to="/settings"
                    className="text-sm text-primary-600 hover:text-primary-700 mt-4 inline-block"
                  >
                    Manage Shared Services
                  </Link>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowInheritanceModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              {availableServices.length > 0 && (
                <button
                  onClick={handleAddInheritance}
                  disabled={linkingService || !inheritanceForm.shared_service_id}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {linkingService ? (
                    <>
                      <span className="animate-spin mr-2">...</span>
                      Linking...
                    </>
                  ) : (
                    <>
                      <GitBranch className="h-4 w-4 mr-2" />
                      Link Service
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Dependency Modal */}
      {showDependencyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Network className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add Dependency</h3>
                    <p className="text-sm text-gray-600">Link this control to another control it depends on</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDependencyModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5 overflow-y-auto max-h-[60vh]">
              {/* Search for controls */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Controls
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={dependencySearchTerm}
                    onChange={(e) => setDependencySearchTerm(e.target.value)}
                    placeholder="Search by name or reference ID..."
                    className="input pl-10 w-full"
                  />
                </div>
              </div>

              {/* Control selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Target Control <span className="text-red-500">*</span>
                </label>
                {loadingAvailableControls ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent mx-auto"></div>
                    <p className="text-sm text-gray-500 mt-2">Loading controls...</p>
                  </div>
                ) : filteredAvailableControls.length > 0 ? (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {filteredAvailableControls.map((ctrl) => (
                      <div
                        key={ctrl.id}
                        onClick={() => setDependencyForm({ ...dependencyForm, target_control_id: ctrl.id })}
                        className={`p-3 cursor-pointer border-b last:border-b-0 hover:bg-gray-50 transition-colors ${
                          dependencyForm.target_control_id === ctrl.id ? 'bg-purple-50 border-l-4 border-l-purple-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{ctrl.name}</p>
                            <p className="text-xs text-gray-500">{ctrl.control_number}</p>
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(ctrl.status)}`}>
                            {ctrl.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 border rounded-lg bg-gray-50">
                    <p className="text-sm text-gray-500">
                      {dependencySearchTerm ? 'No controls match your search' : 'No available controls to add as dependencies'}
                    </p>
                  </div>
                )}
              </div>

              {/* Relationship type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Relationship Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setDependencyForm({ ...dependencyForm, relationship_type: 'blocks' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      dependencyForm.relationship_type === 'blocks'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Lock className={`h-5 w-5 mx-auto mb-1 ${
                      dependencyForm.relationship_type === 'blocks' ? 'text-red-600' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm font-medium ${
                      dependencyForm.relationship_type === 'blocks' ? 'text-red-700' : 'text-gray-700'
                    }`}>Blocks</p>
                    <p className="text-xs text-gray-500 mt-1">Must be compliant first</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDependencyForm({ ...dependencyForm, relationship_type: 'supports' })}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      dependencyForm.relationship_type === 'supports'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Unlock className={`h-5 w-5 mx-auto mb-1 ${
                      dependencyForm.relationship_type === 'supports' ? 'text-blue-600' : 'text-gray-400'
                    }`} />
                    <p className={`text-sm font-medium ${
                      dependencyForm.relationship_type === 'supports' ? 'text-blue-700' : 'text-gray-700'
                    }`}>Supports</p>
                    <p className="text-xs text-gray-500 mt-1">Informational link</p>
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={dependencyForm.notes}
                  onChange={(e) => setDependencyForm({ ...dependencyForm, notes: e.target.value })}
                  placeholder="Describe why this dependency exists..."
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowDependencyModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDependency}
                disabled={addingDependency || !dependencyForm.target_control_id}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {addingDependency ? (
                  <>
                    <span className="animate-spin mr-2">...</span>
                    Adding...
                  </>
                ) : (
                  <>
                    <Network className="h-4 w-4 mr-2" />
                    Add Dependency
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #94: Control History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <History className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Change History</h3>
                    <p className="text-sm text-gray-600">
                      {control?.control_number} - {control?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Stats Summary */}
              {historyStats && (
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-2xl font-bold text-gray-900">{historyStats.total_changes}</p>
                    <p className="text-xs text-gray-500">Total Changes</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-2xl font-bold text-gray-900">{historyStats.updates}</p>
                    <p className="text-xs text-gray-500">Updates</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border">
                    <p className="text-2xl font-bold text-gray-900">{historyStats.status_changes}</p>
                    <p className="text-xs text-gray-500">Status Changes</p>
                  </div>
                </div>
              )}

              {/* Filter Tabs */}
              <div className="mt-4 flex gap-2">
                {(['all', 'created', 'updated', 'status_change'] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => {
                      setHistoryFilter(filter)
                      // Re-fetch with new filter
                      setTimeout(() => fetchHistory(), 0)
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      historyFilter === filter
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border'
                    }`}
                  >
                    {filter === 'all' ? 'All' :
                     filter === 'status_change' ? 'Status Changes' :
                     filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {loadingHistory ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-3">Loading history...</p>
                </div>
              ) : history.length > 0 ? (
                <div className="space-y-4">
                  {history.map((entry, index) => {
                    const ChangeIcon = getChangeTypeIcon(entry.change_type)
                    const changedFields = entry.previous_values && entry.new_values
                      ? Object.keys(entry.new_values)
                      : []

                    return (
                      <div
                        key={entry.id}
                        className="relative pl-8 pb-4 border-b last:border-b-0"
                      >
                        {/* Timeline line */}
                        {index < history.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200" />
                        )}

                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-0 w-6 h-6 rounded-full flex items-center justify-center ${getChangeTypeColor(entry.change_type)}`}>
                          <ChangeIcon className="h-3 w-3" />
                        </div>

                        {/* Content */}
                        <div>
                          <div className="flex items-start justify-between mb-1">
                            <div>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getChangeTypeColor(entry.change_type)}`}>
                                {entry.change_type === 'status_change' ? 'Status Change' :
                                 entry.change_type.charAt(0).toUpperCase() + entry.change_type.slice(1)}
                              </span>
                              {entry.version && (
                                <span className="ml-2 text-xs text-gray-400">v{entry.version}</span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDateTime(entry.changed_at)}
                            </span>
                          </div>

                          <p className="text-sm text-gray-900 mb-1">
                            {entry.change_summary || 'Control updated'}
                          </p>

                          <p className="text-xs text-gray-500 mb-2">
                            by {entry.changed_by_display_name || entry.changed_by_username || 'Unknown user'}
                          </p>

                          {/* Field diffs */}
                          {changedFields.length > 0 && (
                            <div className="mt-3 bg-gray-50 rounded-lg p-3 border">
                              <p className="text-xs font-medium text-gray-500 mb-2 uppercase tracking-wider">
                                Changes
                              </p>
                              <div className="space-y-1">
                                {changedFields.map((field) => (
                                  renderFieldDiff(
                                    field,
                                    entry.previous_values?.[field],
                                    entry.new_values?.[field]
                                  )
                                ))}
                              </div>
                            </div>
                          )}

                          {/* For created events, show initial values */}
                          {entry.change_type === 'created' && entry.new_values && (
                            <div className="mt-3 bg-green-50 rounded-lg p-3 border border-green-100">
                              <p className="text-xs font-medium text-green-700 mb-2 uppercase tracking-wider">
                                Initial Values
                              </p>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                {Object.entries(entry.new_values).slice(0, 6).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>{' '}
                                    <span className="text-gray-900">
                                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') :
                                       Array.isArray(value) ? value.join(', ') || '(none)' :
                                       String(value || '-')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No history records found</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {historyFilter !== 'all'
                      ? `No ${historyFilter.replace('_', ' ')} events for this control.`
                      : 'Changes to this control will appear here.'}
                  </p>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="btn-secondary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue #72: Control Exception Request Modal */}
      {showExceptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b bg-gradient-to-r from-orange-50 to-amber-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Shield className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Request Exception</h3>
                    <p className="text-sm text-gray-600">
                      {control?.control_number} - {control?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExceptionModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              {/* Exception Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exception Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['exception', 'waiver', 'compensating_control'] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setExceptionForm({ ...exceptionForm, exception_type: type })}
                      className={`p-3 rounded-lg border-2 transition-all text-center ${
                        exceptionForm.exception_type === type
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className={`text-sm font-medium ${
                        exceptionForm.exception_type === type ? 'text-orange-700' : 'text-gray-700'
                      }`}>
                        {type === 'exception' ? 'Exception' :
                         type === 'waiver' ? 'Waiver' : 'Compensating Control'}
                      </p>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {exceptionForm.exception_type === 'exception' && 'Temporary deviation from control requirements'}
                  {exceptionForm.exception_type === 'waiver' && 'Formal approval to not implement this control'}
                  {exceptionForm.exception_type === 'compensating_control' && 'Alternative control that provides equivalent protection'}
                </p>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason / Justification <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={exceptionForm.reason}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, reason: e.target.value })}
                  placeholder="Explain why this exception is needed..."
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>

              {/* Risk Acceptance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Risk Acceptance Statement
                </label>
                <textarea
                  value={exceptionForm.risk_acceptance}
                  onChange={(e) => setExceptionForm({ ...exceptionForm, risk_acceptance: e.target.value })}
                  placeholder="Describe the residual risk and why it's acceptable..."
                  rows={2}
                  className="input w-full resize-none"
                />
              </div>

              {/* Compensating Control Description */}
              {exceptionForm.exception_type === 'compensating_control' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Compensating Control Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={exceptionForm.compensating_control_description}
                    onChange={(e) => setExceptionForm({ ...exceptionForm, compensating_control_description: e.target.value })}
                    placeholder="Describe the compensating control and how it provides equivalent protection..."
                    rows={3}
                    className="input w-full resize-none"
                  />
                </div>
              )}

              {/* Valid From/Until Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={new Date(exceptionForm.valid_from).toISOString().split('T')[0]}
                    onChange={(e) => setExceptionForm({
                      ...exceptionForm,
                      valid_from: new Date(e.target.value).getTime()
                    })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    value={exceptionForm.valid_until ? new Date(exceptionForm.valid_until).toISOString().split('T')[0] : ''}
                    onChange={(e) => setExceptionForm({
                      ...exceptionForm,
                      valid_until: e.target.value ? new Date(e.target.value).getTime() : null
                    })}
                    min={new Date(exceptionForm.valid_from).toISOString().split('T')[0]}
                    className="input w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave blank for permanent exception</p>
                </div>
              </div>

              {/* Review Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review Frequency
                </label>
                <select
                  value={exceptionForm.review_frequency || ''}
                  onChange={(e) => setExceptionForm({
                    ...exceptionForm,
                    review_frequency: e.target.value as any || null
                  })}
                  className="input w-full"
                >
                  <option value="">No scheduled review</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annually">Semi-Annually</option>
                  <option value="annually">Annually</option>
                </select>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 rounded-b-xl flex gap-3">
              <button
                onClick={() => setShowExceptionModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestException}
                disabled={submittingException || !exceptionForm.reason.trim() ||
                  (exceptionForm.exception_type === 'compensating_control' && !exceptionForm.compensating_control_description.trim())}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {submittingException ? (
                  <>
                    <span className="animate-spin mr-2">...</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
